/**
 * The quest file (ADR-139 decision 2): one file per quest, plain serialisable data, checked here and by nothing else.
 * A quest is a type, a title and summary, how it repeats, and 1 to 16 steps, each with objectives that must all be met,
 * rewards, an optional `when` and a `next` that names the following step, ends the quest or branches on conditions.
 * Text is line records (`../content/line.ts`); conditions are the dialogue's closed set; objectives and rewards are
 * closed sets (`./objectives.ts`). Pure.
 */

import { checkConditions, lineMessage, suffixOf, type DialogueCondition } from "../content/dialogue.ts";
import { lineProblems, type LineRecord } from "../content/line.ts";
import { checkObjective, checkReward, isPlain, type QuestObjective, type QuestReward } from "./objectives.ts";

/**
 * The quest types: Bible 12.1's seven and the town quest (answer 4, decision 11).
 */
export const QUEST_TYPES = Object.freeze(["main", "side", "daily", "weekly", "discovery", "resonance", "duel", "town"] as const);

/**
 * A quest's type, which sets its log grouping and whether it may be abandoned.
 */
export type QuestType = (typeof QUEST_TYPES)[number];

/**
 * The story types, which a character may never abandon (answer 2).
 */
export const STORY_QUEST_TYPES = Object.freeze(["main", "resonance"] as const);

/**
 * A quest's id: lower case, digits and underscores, at most 48 characters.
 */
export const QUEST_ID_PATTERN = /^[a-z0-9_]{1,48}$/;

/**
 * A step's id: lower case, digits and underscores, at most 32 characters.
 */
export const STEP_ID_PATTERN = /^[a-z0-9_]{1,32}$/;

/**
 * A fact name (ADR-120 decision 1.3), which `<quest>.<step>` must also be.
 */
export const FACT_NAME_PATTERN = /^[a-z0-9_.]{1,64}$/;

/**
 * A town's id, as a zone's `town` field names it.
 */
export const TOWN_ID_PATTERN = /^[a-z0-9_]{1,48}$/;

/**
 * The most steps one quest holds.
 */
export const MAX_QUEST_STEPS = 16;

/**
 * The most objectives one step holds; every one must be met.
 */
export const MAX_STEP_OBJECTIVES = 8;

/**
 * The most rewards one step gives.
 */
export const MAX_STEP_REWARDS = 8;

/**
 * The most branches one step's `next` lists.
 */
export const MAX_STEP_BRANCHES = 8;

/**
 * The fewest in-game days a town quest's window lasts.
 */
export const MIN_TOWN_DAYS = 1;

/**
 * The most in-game days a town quest's window lasts.
 */
export const MAX_TOWN_DAYS = 28;

/**
 * How a quest repeats: never, daily at 00:00 UTC, weekly on Monday 00:00 UTC, or per window of a town (decision 11).
 * A town quest that leaves `everyDays` out takes its town's `townQuestDays`.
 */
export type QuestRepeat = null | "daily" | "weekly" | { readonly town: string; readonly everyDays?: number };

/**
 * One branch of a step's `next`: the first whose conditions hold wins.
 */
export interface QuestBranch {
  readonly when: readonly DialogueCondition[];
  readonly step: string;
}

/**
 * One step of a quest.
 */
export interface QuestStep {
  readonly id: string;
  /** What the log shows while the character is on the step. */
  readonly text: LineRecord;
  readonly objectives: readonly QuestObjective[];
  readonly rewards: readonly QuestReward[];
  /** Conditions that must hold for the step to be offered. */
  readonly when?: readonly DialogueCondition[];
  /** The following step, null for the last, or branches whose first holding entry wins. */
  readonly next: string | null | readonly QuestBranch[];
}

/**
 * A valid quest file.
 */
export interface Quest {
  readonly id: string;
  /** One past the highest line number the file has ever used. */
  readonly nextLine: number;
  readonly type: QuestType;
  readonly title: LineRecord;
  readonly summary: LineRecord;
  readonly repeat: QuestRepeat;
  /** Marks the quest's steps for the party share (ADR-120 decision 11). */
  readonly party: boolean;
  /** Conditions a start checks. */
  readonly requires?: readonly DialogueCondition[];
  /** Where a character on a step this revision removed goes: old step id to a step of this file. */
  readonly moved?: { readonly [step: string]: string };
  readonly steps: readonly QuestStep[];
}

/**
 * One fault: the file, the step it is in (null outside the steps or when the step has no valid id) and the field, a
 * path inside the step or the file.
 */
export interface QuestProblem {
  readonly file: string;
  readonly step: string | null;
  readonly field: string;
  readonly message: string;
}

/**
 * The verdict on one file.
 */
export type QuestVerdict = { readonly ok: true; readonly quest: Quest } | { readonly ok: false; readonly problems: readonly QuestProblem[] };

/**
 * The fields a quest file may hold.
 */
const TOP_FIELDS = ["id", "nextLine", "type", "title", "summary", "repeat", "party", "requires", "moved", "steps"] as const;

/**
 * The fields a step may hold.
 */
const STEP_FIELDS = ["id", "text", "objectives", "rewards", "when", "next"] as const;

/**
 * Whether a quest of a type may be abandoned and taken again (answer 2): every type but the story ones.
 *
 * @param type The quest's type.
 * @returns True when it may.
 */
export function questMayBeAbandoned(type: QuestType): boolean {
  return !(STORY_QUEST_TYPES as readonly string[]).includes(type);
}

/**
 * Checks `repeat` against the type: `daily` and `weekly` exactly for those types, a town and window exactly for `town`,
 * null for every other type.
 *
 * @param type The type as read, already checked.
 * @param repeat The repeat as read.
 * @param report Records a problem against the file.
 */
function checkRepeat(type: unknown, repeat: unknown, report: (field: string, message: string) => void): void {
  if (repeat === undefined) {
    report("repeat", "repeat must be null, daily, weekly or a town's window");
    return;
  }
  if (type === "daily" || type === "weekly") {
    if (repeat !== type) {
      report("repeat", `a ${type} quest must repeat ${type}`);
    }
    return;
  }
  if (type !== "town") {
    if (repeat !== null) {
      report("repeat", `a ${String(type)} quest does not repeat; repeat must be null`);
    }
    return;
  }
  if (!isPlain(repeat)) {
    report("repeat", "a town quest repeats by { town, everyDays }");
    return;
  }
  for (const key of Object.keys(repeat)) {
    if (key !== "town" && key !== "everyDays") {
      report(`repeat.${key}`, `${key} is not a field of a town repeat`);
    }
  }
  if (typeof repeat["town"] !== "string" || !TOWN_ID_PATTERN.test(repeat["town"])) {
    report("repeat.town", "town must name a town: lower case, digits and _");
  }
  const days = repeat["everyDays"];
  if (days !== undefined && (typeof days !== "number" || !Number.isInteger(days) || days < MIN_TOWN_DAYS || days > MAX_TOWN_DAYS)) {
    report("repeat.everyDays", `everyDays must be a whole number from ${MIN_TOWN_DAYS} to ${MAX_TOWN_DAYS}, or left out for the town's own`);
  }
}

/**
 * Checks a list of 0 or `min` to `max` entries, each by `check`.
 *
 * @param value The list as read.
 * @param min The fewest entries.
 * @param max The most entries.
 * @param what The list's name, for the sentence.
 * @param check Checks one entry.
 * @param report Records a problem inside the step.
 */
function checkList(value: unknown, min: number, max: number, what: string, check: (entry: unknown, report: (field: string, message: string) => void, path: string) => void, report: (field: string, message: string) => void): void {
  if (!Array.isArray(value) || value.length < min || value.length > max) {
    report(what, `${what} must be a list of ${min} to ${max}`);
    return;
  }
  value.forEach((entry, at) => check(entry, report, `${what}[${at}]`));
}

/**
 * Validates a quest file: the fields and the repeat rule per type, every line record by the line validator (no line has
 * a speaker), the conditions, objectives and rewards as closed sets with reserved kinds refused, the graph (unique step
 * ids, every `next` and branch names a step, every step reachable from the first, a step that ends the quest, at most
 * 16 steps), each `<quest>.<step>` a valid fact name, and the `moved` rule: every `moved` entry leads from a step the
 * file lacks to one it has, and when the previous published revision is given, every step it had and this one lacks
 * has an entry.
 *
 * @param value The parsed file.
 * @param fileId The file's name without `.json`, which `id` must equal.
 * @param previous The previous published revision, when there is one.
 * @returns The quest, the same object as `value` (not a copy), or every problem found, each naming the file, the step
 *   and the field.
 * @example
 * validateQuest(JSON.parse(text), "ember_whisper", published);
 */
export function validateQuest(value: unknown, fileId: string, previous?: Quest): QuestVerdict {
  const problems: QuestProblem[] = [];
  /**
   * Records one problem against the file.
   * @param step The step it is in, or null.
   * @param field The field, a path inside the step or the file.
   * @param message What is wrong.
   */
  const at = (step: string | null, field: string, message: string): void => {
    problems.push({ file: fileId, step, field, message });
  };
  /**
   * Records one problem outside the steps.
   * @param field The field.
   * @param message What is wrong.
   */
  const top = (field: string, message: string): void => at(null, field, message);
  if (!isPlain(value)) {
    top("", "a quest must be a JSON object");
    return { ok: false, problems };
  }
  for (const key of Object.keys(value)) {
    if (!(TOP_FIELDS as readonly string[]).includes(key)) {
      top(key, `${key} is not a quest field`);
    }
  }
  const id = value["id"];
  if (typeof id !== "string" || !QUEST_ID_PATTERN.test(id)) {
    top("id", "id must be 1 to 48 lower-case letters, digits and _");
  } else if (id !== fileId) {
    top("id", `id must be ${fileId}, the file's name, not ${id}`);
  }
  const type = value["type"];
  if (typeof type !== "string" || !(QUEST_TYPES as readonly string[]).includes(type)) {
    top("type", `type must be ${QUEST_TYPES.join(", ")}`);
  } else {
    checkRepeat(type, value["repeat"], top);
  }
  if (typeof value["party"] !== "boolean") {
    top("party", "party must be true or false");
  }
  if (value["requires"] !== undefined) {
    if (!Array.isArray(value["requires"])) {
      top("requires", "requires must be a list of conditions");
    } else {
      checkConditions(value["requires"], top, "requires");
    }
  }

  const lines: unknown[] = [];
  const sites: { readonly step: string | null; readonly field: string }[] = [];
  /**
   * Queues a line record for the line validator, remembering where it sits.
   * @param line The record as read.
   * @param step The step it is in, or null.
   * @param field The path of the record.
   */
  const collect = (line: unknown, step: string | null, field: string): void => {
    lines.push(line);
    sites.push({ step, field });
  };
  collect(value["title"], null, "title");
  collect(value["summary"], null, "summary");

  const steps = value["steps"];
  const stepIds: string[] = [];
  const edges = new Map<string, string[]>();
  let ends = false;
  if (!Array.isArray(steps) || steps.length < 1 || steps.length > MAX_QUEST_STEPS) {
    top("steps", `steps must be a list of 1 to ${MAX_QUEST_STEPS} steps`);
  } else {
    const named = steps.map((step) => (isPlain(step) && typeof step["id"] === "string" && STEP_ID_PATTERN.test(step["id"]) ? step["id"] : null));
    const known = new Set(named.filter((name): name is string => name !== null));
    steps.forEach((step, index) => {
      if (!isPlain(step)) {
        top(`steps[${index}]`, "a step must be an object with id, text, objectives, rewards and next");
        return;
      }
      const name = named[index] ?? null;
      const prefix = name === null ? `steps[${index}].` : "";
      /**
       * Records one problem inside this step.
       * @param field The field inside the step.
       * @param message What is wrong.
       */
      const report = (field: string, message: string): void => at(name, `${prefix}${field}`, message);
      if (name === null) {
        report("id", "step id must be 1 to 32 lower-case letters, digits and _");
      } else if (stepIds.includes(name)) {
        report("id", `step id ${name} is used twice`);
      } else {
        stepIds.push(name);
        if (typeof id === "string" && !FACT_NAME_PATTERN.test(`${id}.${name}`)) {
          report("id", `the step's fact name ${id}.${name} is longer than a fact name's 64 characters; shorten the quest or step id`);
        }
      }
      for (const key of Object.keys(step)) {
        if (!(STEP_FIELDS as readonly string[]).includes(key)) {
          report(key, `${key} is not a field of a step`);
        }
      }
      collect(step["text"], name, `${prefix}text`);
      checkList(step["objectives"], 1, MAX_STEP_OBJECTIVES, "objectives", checkObjective, report);
      checkList(step["rewards"], 0, MAX_STEP_REWARDS, "rewards", checkReward, report);
      if (step["when"] !== undefined) {
        checkConditions(step["when"], report, "when");
      }
      const out: string[] = [];
      const next = step["next"];
      if (next === null) {
        ends = true;
      } else if (typeof next === "string") {
        if (known.has(next)) {
          out.push(next);
        } else {
          report("next", `next names ${next}, which is not a step`);
        }
      } else if (Array.isArray(next)) {
        if (next.length < 1 || next.length > MAX_STEP_BRANCHES) {
          report("next", `a branching next must list 1 to ${MAX_STEP_BRANCHES} branches`);
        }
        next.forEach((branch, index) => {
          const here = `next[${index}]`;
          if (!isPlain(branch)) {
            report(here, "a branch must be an object with when and step");
            return;
          }
          for (const key of Object.keys(branch)) {
            if (key !== "when" && key !== "step") {
              report(`${here}.${key}`, `${key} is not a field of a branch`);
            }
          }
          checkConditions(branch["when"], report, `${here}.when`);
          const target = branch["step"];
          if (typeof target === "string" && known.has(target)) {
            out.push(target);
          } else {
            report(`${here}.step`, `step names ${String(target)}, which is not a step`);
          }
        });
      } else {
        report("next", "next must name a step, be null for the last, or list branches");
      }
      if (name !== null) {
        edges.set(name, [...(edges.get(name) ?? []), ...out]);
      }
    });
    if (!ends) {
      top("steps", "no step ends the quest; give the last step next: null");
    }
    const first = named[0];
    if (first !== null && first !== undefined) {
      const reached = new Set<string>([first]);
      const queue = [first];
      for (let head = 0; head < queue.length; head += 1) {
        for (const target of edges.get(queue[head] as string) ?? []) {
          if (!reached.has(target)) {
            reached.add(target);
            queue.push(target);
          }
        }
      }
      for (const name of stepIds) {
        if (!reached.has(name)) {
          at(name, "id", "no step leads to this step from the first");
        }
      }
    }
  }

  const moved = value["moved"];
  const movedFrom = new Set<string>();
  if (moved !== undefined) {
    if (!isPlain(moved)) {
      top("moved", "moved must be an object of removed step ids to steps of this file");
    } else {
      for (const [from, to] of Object.entries(moved)) {
        movedFrom.add(from);
        if (stepIds.includes(from)) {
          top(`moved.${from}`, `${from} is still a step of this file, so nobody is moved from it`);
        } else if (typeof to !== "string" || !stepIds.includes(to)) {
          top(`moved.${from}`, `moved names ${String(to)}, which is not a step of this file`);
        }
      }
    }
  }
  if (previous !== undefined && Array.isArray(steps)) {
    for (const step of previous.steps) {
      if (!stepIds.includes(step.id) && !movedFrom.has(step.id)) {
        top(`moved.${step.id}`, `the published revision has step ${step.id}, which this one removes; name where its characters go in moved`);
      }
    }
  }

  for (const problem of lineProblems(lines, value["nextLine"])) {
    if (problem.kind === "invalidNextLine") {
      top("nextLine", lineMessage(problem));
      continue;
    }
    const site = sites[problem.index] as (typeof sites)[number];
    at(site.step, `${site.field}${suffixOf(problem)}`, lineMessage(problem));
  }
  lines.forEach((line, index) => {
    const site = sites[index] as (typeof sites)[number];
    if (isPlain(line) && line["speaker"] !== null) {
      at(site.step, `${site.field}.speaker`, "a quest's lines have no speaker; speaker must be null");
    }
  });

  return problems.length > 0 ? { ok: false, problems } : { ok: true, quest: value as unknown as Quest };
}
